import { toUserFacingError } from '../utils/errorMessages';
import { refreshAccessToken } from './session';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';

/** The signed-in session's access token, or null when nobody is signed in. */
function storedToken(): string | null {
  try {
    return window.localStorage.getItem('policyora_token');
  } catch {
    // Storage can be blocked; an anonymous request is the right fallback.
    return null;
  }
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
  // Internal: prevents a refresh loop if the renewed token is also rejected.
  allowRetry = true,
): Promise<T> {
  const url = `${API_BASE_URL}${path}`;

  const { headers, ...rest } = options;

  // The caller used to have to remember the Authorization header, and a
  // request that forgot it was not refused — it was served as though nobody
  // were signed in. Search was the visible case: the results page sent no
  // token, so a customer's results were not scoped to the jurisdictions their
  // plan covers, and the listing beside it was. Attach the stored token here;
  // a caller that passes its own header still wins.
  const stored = storedToken();
  const withAuth: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(stored ? { Authorization: `Bearer ${stored}` } : {}),
    ...((headers as Record<string, string> | undefined) ?? {}),
  };

  const response = await fetch(url, {
    ...rest,
    headers: withAuth,
  });

  // Access tokens last 15 minutes. A 401 usually means this request simply
  // straddled an expiry, so renew once and retry rather than surfacing an error
  // or bouncing the user to the login screen. Refreshing is deduplicated, so
  // several requests failing together cause only one refresh.
  if (response.status === 401 && allowRetry && !path.startsWith('/api/v1/auth/')) {
    const token = await refreshAccessToken();
    if (token) {
      const retryHeaders = { ...(headers as Record<string, string> | undefined) };
      // Set unconditionally: the header may have come from the default above
      // rather than from the caller, and the retry must carry the new token.
      retryHeaders.Authorization = `Bearer ${token}`;
      return apiRequest<T>(path, { ...rest, headers: retryHeaders }, false);
    }
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    // Attach the HTTP status so callers can tell "the resource legitimately does
    // not exist" (404) from "the request failed" (401/5xx/network). Without this
    // every failure looked identical, and BillingContext treated a server blip
    // as "this user has no subscription" — locking paying customers out.
    const err = new Error(
      toUserFacingError(errorBody, `Request failed with status ${response.status}`),
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}
